import { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useI18n } from '../../context/I18nContext';
import { useLogin } from '../../context/LoginContext';
import fhirService from '../../services/fhirService';
import VzdSearchResults from './VzdSearchResults';
import logger from '../../utils/logger';
import './VzdSearchModal.css';

const TABS = {
  PERSON: 'person',
  HEALTHCARE_SERVICE: 'healthcareService',
};

const VzdSearchModal = ({ isOpen, onClose, onSelectUser, selectedUserIds }) => {
  const { t } = useI18n();
  const { isInsuredHomeserver } = useLogin();
  const isClassifying = isInsuredHomeserver === null;
  const [activeTab, setActiveTab] = useState(TABS.PERSON);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advancedFields, setAdvancedFields] = useState({});
  const [results, setResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleTabChange = useCallback((tab) => {
    // Abort any pending search
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setActiveTab(tab);
    setResults(null);
    setError(null);
    setAdvancedFields({});
    setShowAdvanced(false);
    setSearchTerm('');
  }, []);

  const handleSearch = useCallback(async () => {
    // Block while the homeserver classification is unresolved — an insured user
    // racing the lookup must never see a hide-versicherte endpoint.
    if (isClassifying) return;

    const trimmedSearch = searchTerm.trim();
    if (!trimmedSearch && !Object.values(advancedFields).some(v => v?.trim())) {
      return; // Reject empty searches
    }

    // Abort previous search
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsSearching(true);
    setError(null);
    setResults(null);

    const searchOptions = { excludeHiddenFromInsured: isInsuredHomeserver === true };

    try {
      let searchResults;
      if (activeTab === TABS.PERSON) {
        const params = {};
        if (trimmedSearch) params['practitioner.name:contains'] = trimmedSearch;
        if (advancedFields.telematikId?.trim()) params['practitioner.identifier:contains'] = advancedFields.telematikId.trim();
        if (advancedFields.endpointName?.trim()) params['endpoint.name:contains'] = advancedFields.endpointName.trim();
        searchResults = await fhirService.searchPractitioners(params, controller.signal, searchOptions);
      } else {
        const params = {};
        if (trimmedSearch) params['name:contains'] = trimmedSearch;
        if (advancedFields.orgName?.trim()) params['organization.name:contains'] = advancedFields.orgName.trim();
        if (advancedFields.orgType?.trim()) params['organization.type'] = advancedFields.orgType.trim();
        if (advancedFields.location?.trim()) params['address'] = advancedFields.location.trim();
        searchResults = await fhirService.searchHealthcareServices(params, controller.signal, searchOptions);
      }
      setResults(searchResults);
    } catch (err) {
      if (err.name !== 'AbortError') {
        logger.error('VZD search failed:', err);
        setError(t('VzdSearch.errorSearchFailed'));
      }
    } finally {
      setIsSearching(false);
    }
  }, [searchTerm, advancedFields, activeTab, t, isClassifying, isInsuredHomeserver]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }, [handleSearch]);

  const handleAdvancedFieldChange = useCallback((field, value) => {
    setAdvancedFields(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleClose = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setSearchTerm('');
    setResults(null);
    setError(null);
    setShowAdvanced(false);
    setAdvancedFields({});
    setActiveTab(TABS.PERSON);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="vzd-search-modal-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) handleClose();
    }}>
      <div className="vzd-search-modal">
        {/* Header */}
        <div className="vzd-search-header">
          <h2>{t('VzdSearch.modalTitle')}</h2>
          <button className="vzd-search-close" onClick={handleClose} aria-label={t('VzdSearch.closeButton')}>
            &times;
          </button>
        </div>

        {/* Tabs */}
        <div className="vzd-search-tabs">
          <button
            className={`vzd-search-tab ${activeTab === TABS.PERSON ? 'active' : ''}`}
            onClick={() => handleTabChange(TABS.PERSON)}
          >
            {t('VzdSearch.personTab')}
          </button>
          <button
            className={`vzd-search-tab ${activeTab === TABS.HEALTHCARE_SERVICE ? 'active' : ''}`}
            onClick={() => handleTabChange(TABS.HEALTHCARE_SERVICE)}
          >
            {t('VzdSearch.healthcareServiceTab')}
          </button>
        </div>

        {/* Search area */}
        <div className="vzd-search-area">
          <div className="vzd-search-input-row">
            <input
              ref={searchInputRef}
              type="text"
              className="vzd-search-input"
              placeholder={t('VzdSearch.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button
              className="vzd-search-button-action"
              onClick={handleSearch}
              disabled={isSearching || isClassifying}
            >
              {isSearching
                ? t('VzdSearch.loadingResults')
                : isClassifying
                  ? t('VzdSearch.verifyingDirectoryAccess')
                  : t('VzdSearch.searchButton')}
            </button>
          </div>

          <button
            className="vzd-advanced-toggle"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            <ion-icon name={showAdvanced ? 'chevron-up-outline' : 'chevron-down-outline'} />
            {t('VzdSearch.advancedToggle')}
          </button>

          {showAdvanced && (
            <div className="vzd-advanced-fields">
              {activeTab === TABS.PERSON ? (
                <>
                  <div className="vzd-field-group">
                    <label>{t('VzdSearch.telematikIdField')}</label>
                    <input
                      type="text"
                      value={advancedFields.telematikId || ''}
                      onChange={(e) => handleAdvancedFieldChange('telematikId', e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                  <div className="vzd-field-group">
                    <label>{t('VzdSearch.endpointNameField')}</label>
                    <input
                      type="text"
                      value={advancedFields.endpointName || ''}
                      onChange={(e) => handleAdvancedFieldChange('endpointName', e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="vzd-field-group">
                    <label>{t('VzdSearch.orgNameField')}</label>
                    <input
                      type="text"
                      value={advancedFields.orgName || ''}
                      onChange={(e) => handleAdvancedFieldChange('orgName', e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                  <div className="vzd-field-group">
                    <label>{t('VzdSearch.orgTypeField')}</label>
                    <input
                      type="text"
                      value={advancedFields.orgType || ''}
                      onChange={(e) => handleAdvancedFieldChange('orgType', e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                  <div className="vzd-field-group">
                    <label>{t('VzdSearch.locationField')}</label>
                    <input
                      type="text"
                      value={advancedFields.location || ''}
                      onChange={(e) => handleAdvancedFieldChange('location', e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Results area */}
        <div className="vzd-search-results-area">
          {error && (
            <div className="vzd-search-error">
              <ion-icon name="alert-circle-outline" />
              <span>{error}</span>
            </div>
          )}
          {!error && results !== null && (
            <VzdSearchResults
              results={results}
              type={activeTab}
              onSelectUser={onSelectUser}
              selectedUserIds={selectedUserIds}
            />
          )}
          {!error && results === null && !isSearching && (
            <div className="vzd-search-empty">
              <ion-icon name={isClassifying ? 'hourglass-outline' : 'search-outline'} />
              <span>
                {isClassifying
                  ? t('VzdSearch.verifyingDirectoryAccess')
                  : t('VzdSearch.searchEmptyState')}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="vzd-search-footer">
          <button className="vzd-search-done-button" onClick={handleClose}>
            {t('VzdSearch.doneButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

VzdSearchModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectUser: PropTypes.func.isRequired,
  selectedUserIds: PropTypes.instanceOf(Set),
};

export default VzdSearchModal;
